import type { RedemptionRequest } from "../modals/types";
import { DataTable } from "@/components/shared/data-table";
import { createColumns } from "./columns";

interface RedemptionStatusTableProps {
  requests: RedemptionRequest[];
  onViewRequest: (request: RedemptionRequest) => void;
  onCancelRequest: (request: RedemptionRequest) => void;
  onApprove?: (request: RedemptionRequest) => void;
  onReject?: (request: RedemptionRequest) => void;
  username?: string | null;
  userPosition?: string | null;
  onRefresh?: () => void;
  refreshing?: boolean;
  loading: boolean;
  error: string | null;
  fillHeight?: boolean;
}

export function RedemptionStatusTable({
  requests,
  onViewRequest,
  onCancelRequest,
  onApprove,
  onReject,
  username,
  userPosition,
  onRefresh,
  refreshing = false,
  loading,
  error,
  fillHeight,
}: RedemptionStatusTableProps) {
  const columns = createColumns({
    onViewRequest,
    onCancelRequest,
    onApprove,
    onReject,
    username,
    userPosition,
  });

  // Custom global filter function that searches across multiple fields
  const globalFilterFn = (row: any, columnId: string, filterValue: string) => {
    const request = row.original as RedemptionRequest;
    const searchLower = String(filterValue).toLowerCase();

    // Search in: request ID, customer name, item names, total points, status
    const searchableFields = [
      String(request.id),
      request.requested_for_name?.toLowerCase() || "",
      request.status_display?.toLowerCase() || "",
      String(request.total_points),
      ...(request.items?.map((item) => item.product_name.toLowerCase()) || []),
      ...(request.items?.map((item) => item.product_code.toLowerCase()) || []),
    ];

    return searchableFields.some((field) => field.includes(searchLower));
  };

  return (
    <div
      className={
        fillHeight ? "h-full overflow-hidden relative" : "hidden md:block"
      }
    >
      <div className={fillHeight ? "absolute inset-0" : undefined}>
        <DataTable
          columns={columns}
          data={requests}
          loading={loading}
          error={error}
          onRefresh={onRefresh}
          refreshing={refreshing}
          searchPlaceholder="Search by Request ID, Customer, Item..."
          globalFilterFn={globalFilterFn}
          showSearch={true}
          showPagination={true}
          showColumnVisibility={true}
          preservePageOnDataRefresh={true}
          pageSize={15}
          initialSorting={[{ id: "date_requested", desc: true }]}
          loadingMessage="Loading requests..."
          emptyMessage="No redemption requests found"
          pageSizeOptions={[15, 50, 100]}
          fillHeight={fillHeight}
        />
      </div>
    </div>
  );
}
